import React, { useContext } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate } from "react-router";
import { logger } from "@platform/browser-logger";
import { Button, Typography } from "@platform/design-system";

import { AuthContext } from "../contexts/authContext";
import useToastNotificationService from "../hooks/useToastNotificationService";

type Props = {
  heading: string;
  email: string;
};

const PasswordSent = ({ heading, email }: Props): React.JSX.Element => {
  const navigate = useNavigate();
  const { createToastNotification } = useToastNotificationService();

  // Reads the typed context directly: the useAuth() hook lives in a .js
  // module, which leaves its return value untyped here.
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error("PasswordSent must be used within an AuthProvider");
  }
  const { resetPassword } = auth;

  const handleBackToSignIn = () => {
    navigate("/");
  };

  const handleResend = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    try {
      await resetPassword(email);
      createToastNotification({
        severity: "success",
        message: "Reset password email was resent",
        title: "Email Sent",
      });
    } catch (error: unknown) {
      logger.error(
        "Error in resend password",
        error instanceof Error ? error : new Error(JSON.stringify(error)),
      );
      createToastNotification({
        severity: "error",
        message: "Something went wrong",
      });
    }
  };

  return (
    <Grid
      size={3}
      margin={"auto 6rem"}
      display={"flex"}
      flexDirection={"column"}
      gap={"32px"}
    >
      {
        <>
          <Typography variant="display-medium" color="brandPrimary">
            {heading}
          </Typography>
          <Typography variant="body-medium">
            If there&apos;s a Centro account associated with <b>{email}</b>, you
            will receive an email in the next few minutes.
          </Typography>

          <Typography variant="body-medium">
            If you didn’t receive the email,
            <Button
              onClick={(e) => handleResend(e)}
              variant="tertiary"
              sx={{ fontWeight: "bold" }}
            >
              Resend.
            </Button>
          </Typography>
          <Box>
            <Button
              onClick={handleBackToSignIn}
              variant="tertiary"
              sx={{ fontWeight: "bold" }}
            >
              Back to sign in{" "}
            </Button>
          </Box>
        </>
      }
    </Grid>
  );
};

export default PasswordSent;
